import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Input, List, Tabs } from "@arco-design/web-react";
import axios from "axios";
import classNames from "classnames";

import { endpoints } from "../../../api/endpoints";
import { UserCard } from "../../../components/UserCard/UserCard";
import { ImportFile } from "./components/ImportFile";
import { User } from "../../../models/Person";
import { routes } from "../../../routes/routes";
import { RootState } from "../../../store/store";
import { setUsersList, setSelectedUser } from "../../../store/adminSlice";

import './UsersList.css';
import '../../../components/PillTabs/pill-tabs.css';

import { currentThemeName } from "../../../theme";
import { getHeaderTextColor } from "../../../utils/cssVariables";
import { useEventIntakeEnabled } from "../../../hooks/useEventIntakeEnabled";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const InputSearch = Input.Search;
const TabPane = Tabs.TabPane;

const BASE_FILTER_OPTIONS = ['mentor', 'mentee', 'admin'] as const;

function matchesRoleFilter(profile: User, filter: string): boolean {
  if (filter === 'admin') {
    return Boolean(profile.isAdmin);
  }
  if (filter === 'mentor') {
    return Boolean(profile.isMentor) && !profile.isAdmin;
  }
  if (filter === 'guest') {
    return Boolean(profile.isGuest) && !profile.isAdmin;
  }
  return !profile.isMentor && !profile.isAdmin && !profile.isGuest;
}

const getFilteredProfiles = (list: User[], searchQuery: string, filter: string[]) => {
  return list.filter((profile) => {
    const fullName = `${profile.name} ${profile.last_name || ''}`.trim().toLowerCase();
    const email = (profile.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    const isMatchSearchString = query
      ? fullName.includes(query) || email.includes(query)
      : true;
    const isMatchFilter = filter.some((item) => matchesRoleFilter(profile, item));
    return isMatchFilter && isMatchSearchString;
  });
};

const useProfilesQuery = () => {
  return useQuery({
    queryKey: ['profiles'],
    queryFn: async () => {
      const response = await axios.get(`${endpoints.admin.getUsers}`);
      return response.data;
    },
  });
};

export function UsersList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const eventIntakeEnabled = useEventIntakeEnabled();

  const usersList = useSelector((state: RootState) => state.admin.usersList);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<string>("mentor");

  const filterOptions = useMemo(() => {
    if (eventIntakeEnabled) {
      return ['mentor', 'mentee', 'guest', 'admin'];
    }
    return [...BASE_FILTER_OPTIONS];
  }, [eventIntakeEnabled]);

  const { data: profiles, isLoading } = useProfilesQuery();

  const filteredProfiles = useMemo(() => {
    return profiles
      ? getFilteredProfiles(profiles, searchQuery, [activeFilter])
      : getFilteredProfiles(usersList, searchQuery, [activeFilter]);
  }, [profiles, searchQuery, activeFilter, usersList]);

  useEffect(() => {
    if (profiles) {
      dispatch(setUsersList(profiles));
    }
  }, [profiles, dispatch]);

  useEffect(() => {
    if (!eventIntakeEnabled && activeFilter === 'guest') {
      setActiveFilter('mentor');
    }
  }, [eventIntakeEnabled, activeFilter]);

  const filterLabel = (item: string) => {
    if (item === 'admin') return t('common.admin');
    if (item === 'mentee') return t('common.mentee');
    if (item === 'guest') return t('common.guest');
    return t('common.mentor');
  };

  const adminRoleLabel = (user: User) => {
    if (user.isSuperAdmin) return t('admin.users_list.super_admin');
    return t('admin.users_list.admin');
  };

  const adminStatusLabel = (user: User) => {
    if (user.accountStatus === 'invited') return t('admin.users_list.status_invited');
    return t('admin.users_list.status_active');
  };

  const isAdminFilter = activeFilter === 'admin';

  return (
    <div className={classNames("users-list", {
      "users-list--black-theme": currentThemeName === "black"
    })}>
      <h1 className="users-list__header" style={{color: getHeaderTextColor()}}>{t('admin.dashboard.user_list')}</h1>
      <div className="users-list__filter-row">
        <Tabs
          activeTab={activeFilter}
          onChange={(key) => setActiveFilter(key)}
          type="rounded"
          className={classNames("users-list__tabs pill-tabs pill-tabs--capitalize", {
            "pill-tabs--black-theme": currentThemeName === "black",
          })}
        >
          {filterOptions.map((item) => (
            <TabPane key={item} title={filterLabel(item)}>
              <span className="users-list__tab-pane-placeholder" aria-hidden />
            </TabPane>
          ))}
        </Tabs>
        <ImportFile />
      </div>
      <InputSearch
        onChange={(e) => setSearchQuery(e)}
        placeholder={t('common.search_placeholder')}
        onPressEnter={() => queryClient.invalidateQueries({queryKey: ['profiles']})}
        className="users-list__search"
      />
      <List
        className="users-list__list"
        dataSource={filteredProfiles}
        bordered={false}
        loading={!filteredProfiles.length && isLoading}
        render={(user: User) => (
          <List.Item key={user.id}>
            <NavLink
              to={`${routes.ADMIN_USER.replace(":id", user.id.toString())}`}
              onClick={() => {
                dispatch(setSelectedUser(user));
              }}
              className="users-list__row-link"
            >
              <UserCard
                user={user}
                roleTag={
                  isAdminFilter
                    ? 'ADMIN'
                    : user.isMentor
                      ? 'MENTOR'
                      : user.isGuest
                        ? 'GUEST'
                        : 'MENTEE'
                }
                hideBooking
                hideActions
                secondaryLine={isAdminFilter ? user.email : undefined}
                metaLine={
                  isAdminFilter
                    ? `${adminRoleLabel(user)} · ${adminStatusLabel(user)}`
                    : undefined
                }
              />
            </NavLink>
          </List.Item>
        )}
      />
    </div>
  );
}
